/**
 * Projeções Financeiras — 9 Cenários (3×3)
 * Investimento (0, 250k, 500k) × Operacional (pessimista, normal, otimista)
 */
import React, { useEffect, useMemo, useState } from 'react';
import { TrendingUp, DollarSign, Users, Target, AlertTriangle, ChevronDown } from 'lucide-react';
import { COLORS } from '../../../components/governance/governanceTheme';
import {
  FINANCIAL_SCENARIOS, INVESTMENT_SCENARIOS
} from './financialScenariosData';
import { getScenarioCatalog } from '../../../services/financialPlanningService';
import { useGovernanceLang } from '../../../contexts/GovernanceLangContext';

const FIN_LABELS = {
  pt: {
    title: 'Projeções Financeiras', subtitle: '9 Cenários — 3 níveis de investimento × 3 operacionais',
    amount: 'Aporte', equityGiven: 'Equity cedido', preMoney: 'Pre-money', founder: 'Founder %',
    matrix: 'Matriz', detail: 'Detalhe', source: 'Fonte',
    revenue: 'Receita', burn: 'Burn', runway: 'Runway', mrr: 'MRR',
    pessimista: 'Pessimista', normal: 'Normal', otimista: 'Otimista',
    opLabels: { pessimista: 'Pessimista', normal: 'Normal', otimista: 'Otimista' },
    matrixTitle: 'Matriz de Cenários (ARR Ano 5)',
    detailTitle: 'Detalhes por Cenário',
    equity: 'Estrutura de Capital',
    investment: 'Investimento', team: 'Time', strategy: 'Estratégia', focusProducts: 'Produtos',
    year5arr: 'ARR Ano 5', pending: 'A definir', noData: 'Sem dados de projeção',
    yearCol: 'Ano', clientsCol: 'Clientes', costsCol: 'Custos', resultCol: 'Resultado', statusCol: 'Op.',
    noValues: 'Sem valores', fallbackMsg: 'Usando dados locais (backend indisponível).',
    hybridPrefix: 'Baseline: ',
    notes: 'Premissas e Hipóteses',
  },
  en: {
    title: 'Financial Projections', subtitle: '9 Scenarios — 3 investment levels × 3 operational',
    amount: 'Investment', equityGiven: 'Equity given', preMoney: 'Pre-money', founder: 'Founder %',
    matrix: 'Matrix', detail: 'Detail', source: 'Source',
    revenue: 'Revenue', burn: 'Burn', runway: 'Runway', mrr: 'MRR',
    pessimista: 'Pessimistic', normal: 'Normal', otimista: 'Optimistic',
    opLabels: { pessimista: 'Pessimistic', normal: 'Normal', otimista: 'Optimistic' },
    matrixTitle: 'Scenario Matrix (Year 5 ARR)',
    detailTitle: 'Scenario Details',
    equity: 'Capital Structure',
    investment: 'Investment', team: 'Team', strategy: 'Strategy', focusProducts: 'Products',
    year5arr: 'Year 5 ARR', pending: 'TBD', noData: 'No projection data',
    yearCol: 'Year', clientsCol: 'Clients', costsCol: 'Costs', resultCol: 'Result', statusCol: 'Op.',
    noValues: 'No values', fallbackMsg: 'Using local data (backend unavailable).',
    hybridPrefix: 'Baseline: ',
    notes: 'Assumptions & Hypotheses',
  },
};

const fmt = (v) => v == null ? '—' : `R$ ${v.toLocaleString('pt-BR')}`;
const fmtK = (v) => {
  if (v == null) return '—';
  if (Math.abs(v) >= 1000000) return `R$ ${(v / 1000000).toFixed(1)}M`;
  if (Math.abs(v) >= 1000) return `R$ ${(v / 1000).toFixed(0)}k`;
  return fmt(v);
};

const SCENARIO_ID_ALIASES = {
  seed_250k_pessimista: '250k_pessimista',
  seed_250k_normal: '250k_normal',
  seed_250k_otimista: '250k_otimista',
  seed_500k_pessimista: '500k_pessimista',
  seed_500k_normal: '500k_normal',
  seed_500k_otimista: '500k_otimista',
};

const resolveLocalScenario = (scenarioKey) => {
  return FINANCIAL_SCENARIOS.find((s) => s.id === scenarioKey)
    || FINANCIAL_SCENARIOS.find((s) => s.id === SCENARIO_ID_ALIASES[scenarioKey])
    || null;
};

// ─── Equity Card ────────────────────────────────────────────────────────────
const EquityCard = ({ inv, t }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-5">
    <h4 className="text-sm font-bold text-gray-900 mb-3">{inv.label}</h4>
    <div className="grid grid-cols-2 gap-3 text-sm">
      <div><span className="text-gray-400">{t.amount}</span> <span className="font-bold">{inv.amount ? fmt(inv.amount) : 'R$ 0'}</span></div>
      <div><span className="text-gray-400">{t.equityGiven}</span> <span className="font-bold text-red-600">{inv.equityGiven}%</span></div>
      <div><span className="text-gray-400">{t.preMoney}</span> <span className="font-bold">{inv.preMoneyValuation ? fmtK(inv.preMoneyValuation) : '—'}</span></div>
      <div><span className="text-gray-400">{t.founder}</span> <span className="font-bold" style={{ color: COLORS.primary }}>{inv.founderOwnership}%</span></div>
    </div>
    <p className="text-xs text-gray-400 mt-3">{inv.notes}</p>
  </div>
);

// ─── Scenario Detail ────────────────────────────────────────────────────────
const ScenarioDetail = ({ scenario, t }) => {
  const [expanded, setExpanded] = useState(false);
  const inv = INVESTMENT_SCENARIOS[scenario.investment];
  const yearly = Array.isArray(scenario.yearly) ? scenario.yearly : [];
  const lastYear = yearly.length > 0 ? yearly[yearly.length - 1] : null;

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: scenario.color }} />
          <div className="text-left">
            <div className="font-bold text-gray-900 text-sm">{scenario.label}</div>
            <div className="text-xs text-gray-500">{scenario.summary}</div>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right hidden sm:block">
            <div className="text-xs text-gray-400">{t.year5arr}</div>
            <div className="font-bold text-sm" style={{ color: scenario.color }}>{lastYear ? fmtK(lastYear.arr) : t.pending}</div>
          </div>
          <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 p-4 space-y-4">
          {/* Meta */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400">{t.investment}</div>
              <div className="font-bold">{inv?.label || '—'}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400">{t.team}</div>
              <div className="font-bold">{scenario.team}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400">{t.strategy}</div>
              <div className="font-bold text-xs">{scenario.strategy}</div>
            </div>
            <div className="bg-gray-50 rounded-lg p-3">
              <div className="text-xs text-gray-400">{t.focusProducts}</div>
              <div className="font-bold text-xs">{(scenario.focusProducts || []).join(', ') || t.pending}</div>
            </div>
          </div>

          {/* Year table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 text-gray-500 font-medium">{t.yearCol}</th>
                  <th className="text-center py-2 text-gray-500 font-medium">{t.clientsCol}</th>
                  <th className="text-center py-2 text-gray-500 font-medium">MRR</th>
                  <th className="text-center py-2 text-gray-500 font-medium">ARR</th>
                  <th className="text-center py-2 text-gray-500 font-medium">{t.costsCol}</th>
                  <th className="text-center py-2 text-gray-500 font-medium">{t.resultCol}</th>
                  <th className="text-center py-2 text-gray-500 font-medium">{t.statusCol}</th>
                </tr>
              </thead>
              <tbody>
                {yearly.map(y => (
                  <tr key={y.year} className="border-b border-gray-50">
                    <td className="py-2 font-bold">{y.year}</td>
                    <td className="py-2 text-center">{y.clients}</td>
                    <td className="py-2 text-center font-medium">{fmtK(y.mrr)}</td>
                    <td className="py-2 text-center font-bold" style={{ color: COLORS.primary }}>{fmtK(y.arr)}</td>
                    <td className="py-2 text-center text-gray-600">{fmtK(y.costs)}</td>
                    <td className={`py-2 text-center font-bold ${y.result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                      {y.result >= 0 ? '+' : ''}{fmtK(y.result)}
                    </td>
                    <td className="py-2 text-center text-xs text-gray-500">{y.runway}</td>
                  </tr>
                ))}
                {yearly.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-3 text-center text-xs text-gray-500">{t.noData}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Matrix View ────────────────────────────────────────────────────────────
const MatrixView = ({ scenarios, t }) => {
  const investments = ['zero', 'seed_250k', 'seed_500k'];
  const operations = ['pessimista', 'normal', 'otimista'];
  const opColors = { pessimista: '#dc2626', normal: '#d97706', otimista: '#16a34a' };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr>
            <th className="p-3 text-left text-gray-500">{t.investment} ↓ / {t.statusCol} →</th>
            {operations.map(op => (
              <th key={op} className="p-3 text-center" style={{ color: opColors[op] }}>{t.opLabels[op]}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {investments.map(inv => (
            <tr key={inv} className="border-t border-gray-100">
              <td className="p-3 font-bold text-gray-900">{INVESTMENT_SCENARIOS[inv].label}</td>
              {operations.map(op => {
                const s = (scenarios || []).find(sc => sc.investment === inv && sc.operational === op);
                const y5 = s?.yearly?.[4];
                return (
                  <td key={op} className="p-3 text-center">
                    {y5 ? (
                      <>
                        <div className="font-bold" style={{ color: opColors[op] }}>{fmtK(y5.arr)}</div>
                        <div className="text-xs text-gray-400">{y5.clients} {t.clientsCol.toLowerCase()}</div>
                        <div className={`text-xs font-medium ${y5.result >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {y5.result >= 0 ? '+' : ''}{fmtK(y5.result)}/mês
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="font-bold text-gray-400">{t.pending}</div>
                        <div className="text-xs text-gray-400">{t.noValues}</div>
                      </>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════════════
const FinancialScenariosPage = () => {
  const [view, setView] = useState('matrix'); // matrix | detail
  const [scenarioCatalog, setScenarioCatalog] = useState(null);
  const [sourceMode, setSourceMode] = useState('loading'); // loading | backend | hybrid | fallback-local

  const { lang } = useGovernanceLang();
  const t = FIN_LABELS[lang] || FIN_LABELS.pt;

  useEffect(() => {
    const loadCatalog = async () => {
      try {
        const res = await getScenarioCatalog();
        const catalog = res?.data || null;
        setScenarioCatalog(catalog);
        setSourceMode(catalog ? 'hybrid' : 'fallback-local');
      } catch (error) {
        console.warn('[FinancialScenariosPage] fallback local scenarios:', error?.message || error);
        setSourceMode('fallback-local');
      }
    };
    loadCatalog();
  }, []);

  const displayedScenarios = useMemo(() => {
    const catalogScenarios = scenarioCatalog?.scenarios;
    if (!Array.isArray(catalogScenarios) || catalogScenarios.length === 0) {
      return FINANCIAL_SCENARIOS;
    }

    return catalogScenarios.map((catalogItem) => {
      const local = resolveLocalScenario(catalogItem.scenario_key);
      return {
        id: catalogItem.scenario_key,
        investment: catalogItem.investment_level,
        operational: catalogItem.operation_case,
        label: local?.label || catalogItem.display_name,
        color: local?.color || '#6b7280',
        summary: local?.summary || 'Sem valores financeiros disponíveis no source-of-truth atual.',
        strategy: local?.strategy || t.pending,
        team: local?.team || t.pending,
        focusProducts: local?.focusProducts || [],
        yearly: local?.yearly || [],
      };
    });
  }, [scenarioCatalog, lang]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/vukz-logo.png" alt="Vukz" className="w-11 h-11 rounded-xl object-contain bg-gray-50 border border-gray-200 p-0.5" onError={(e) => { e.target.style.display = 'none'; }} />
          <div>
          <h1 className="text-2xl font-black text-gray-900">{t.title}</h1>
          <p className="text-sm text-gray-500 mt-1">{t.subtitle}</p>
          </div>
        </div>
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
          <button onClick={() => setView('matrix')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${view === 'matrix' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>{t.matrix}</button>
          <button onClick={() => setView('detail')} className={`px-3 py-1.5 rounded-md text-sm font-medium ${view === 'detail' ? 'bg-white shadow-sm text-gray-900' : 'text-gray-500'}`}>{t.detail}</button>
        </div>
      </div>

      {/* Equity / Dilution */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 mb-3">{t.equity}</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {Object.values(INVESTMENT_SCENARIOS).map(inv => (
            <EquityCard key={inv.id} inv={inv} t={t} />
          ))}
        </div>
      </div>

      {/* Source-of-truth status */}
      {sourceMode === 'fallback-local' && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700">
          {t.fallbackMsg}
        </div>
      )}
      {sourceMode === 'hybrid' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800">
          {t.hybridPrefix}<strong>{scenarioCatalog?.baseline_key || 'zero_normal'}</strong>.
        </div>
      )}

      {/* Matrix or Detail view */}
      {view === 'matrix' ? (
        <div className="bg-white border border-gray-200 rounded-xl p-4">
          <h2 className="text-lg font-bold text-gray-900 mb-3">{t.matrixTitle}</h2>
          <MatrixView scenarios={displayedScenarios} t={t} />
        </div>
      ) : (
        <div className="space-y-3">
          <h2 className="text-lg font-bold text-gray-900">{t.detailTitle}</h2>
          {displayedScenarios.map(s => (
            <ScenarioDetail key={s.id} scenario={s} t={t} />
          ))}
        </div>
      )}

      {/* Notes */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 text-sm">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-yellow-600" />
          <span className="font-bold text-yellow-800">{t.notes}</span>
        </div>
        <ul className="text-yellow-700 space-y-1 text-xs">
          <li>• Valores em BRL. MRR e custos são mensais. ARR = MRR × 12.</li>
          <li>• Créditos Azure (US$ 250k) reduzem custo cloud nos primeiros ~24 meses.</li>
          <li>• Equity R$ 250k → 10% (pre-money R$ 2.25M) | R$ 500k → 18% (pre-money R$ 2.28M).</li>
          <li>• "Resultado" = MRR - Custos fixos mensais (simplificado, não inclui impostos/setup).</li>
          <li>• Cenários são base inicial — editar conforme realidade validar premissas.</li>
          <li>• Para DRE completa: integrar com Financeiro (/governance/fin).</li>
        </ul>
      </div>
    </div>
  );
};

export default FinancialScenariosPage;
